<template>
  <div class="bg-white border border-gray-300 rounded-lg shadow-sm p-6 mb-4">
    <h3 class="flex items-center justify-between mb-4">
      <span class="leading-none">{{ $gettext('Propriété') }} {{ index + 1 }}</span>
      <button
        @click.prevent="$emit('remove')"
        class="text-indigo-600 hover:text-indigo-900 transition-transform duration-200 hover:scale-110 flex items-center"
      >
        <MinusCircleIcon class="size-7 align-middle" aria-hidden="true" />
      </button>
    </h3>

    <div class="space-y-4 flex-1">
      <FormPropertyComboboxField
        v-model="normalized.relationships.property.data"
        :field-path="'relationships.property.data'"
        :field-label="$gettext('Propriété')"
        :errors="errors"
      />

      <FormMoneyField
        v-model="normalized.attributes.listingPrice"
        :field-path="'attributes.listingPrice'"
        :field-label="$gettext('Prix de commercialisation')"
        :errors="errors"
      />

      <FormSelectField
        v-model="normalized.attributes.soldWithinThreeYears"
        :field-path="'attributes.soldWithinThreeYears'"
        :field-label="$gettext('Le bien a-t-il été vendu dans les trois dernières années ?')"
        :field-description="$gettext('Exemple : un bien acquis il y a moins de trois ans et déjà remis en vente — une revente rapide peut interroger la logique économique de l\'opération.')"
        :options="yesNoOptions"
        :errors="errors"
      />

      <FormTextField
        v-model="normalized.relationships.property.data.attributes.surfaceArea"
        :field-path="'relationships.property.data.attributes.surfaceArea'"
        :field-label="$gettext('Superficie (m²)')"
        :errors="errors"
      />

      <FormSelectField
        v-model="normalized.relationships.property.data.relationships.propertyType.data.id"
        :field-path="'relationships.property.data.relationships.propertyType.data.id'"
        :field-label="$gettext('Type de bien')"
        :options="propertyTypeOptions"
        :errors="errors"
      />

      <FormTextField
        v-if="unitIdentifierRequired"
        v-model="normalized.relationships.property.data.attributes.addressApartmentNumber"
        :field-path="'relationships.property.data.attributes.addressApartmentNumber'"
        :field-label="unitIdentifierLabel"
        :errors="errors"
      />

      <FormTextareaField
        v-model="normalized.attributes.details"
        :field-path="'attributes.details'"
        :field-label="$gettext('Détails du bien')"
        :errors="errors"
      />
    </div>
  </div>
</template>

<script setup>
import { reactive, watch, computed } from "vue";
import { MinusCircleIcon } from "@heroicons/vue/24/outline";
import { useGettext } from "vue3-gettext";
import FormPropertyComboboxField from "@/components/form-fields/FormPropertyComboboxField.vue";
import FormMoneyField from "@/components/form-fields/FormMoneyField.vue";
import FormTextField from "@/components/form-fields/FormTextField.vue";
import FormSelectField from "@/components/form-fields/FormSelectField.vue";
import FormTextareaField from "@/components/form-fields/FormTextareaField.vue";
import { usePropertyTypeStore } from "@/stores/propertyType";

const { $gettext } = useGettext();
const propertyTypeStore = usePropertyTypeStore();

const yesNoOptions = [
  { value: "yes", label: $gettext("Oui") },
  { value: "no", label: $gettext("Non") }
];

const propertyTypeOptions = computed(() =>
  propertyTypeStore.sortedPropertyTypes.map((propertyType) => ({
    value: propertyType.id,
    label: propertyType.attributes.name
  }))
);

const selectedPropertyType = computed(() =>
  propertyTypeStore.all.find(
    (t) => t.id === normalized.relationships.property.data.relationships.propertyType.data.id
  )
);

const unitIdentifierRequired = computed(() =>
  selectedPropertyType.value?.attributes?.requiresUnitIdentifier === true
);

const unitIdentifierLabel = computed(() => {
  const typeName = selectedPropertyType.value?.attributes?.name;
  if (typeName === "Garage") return $gettext("Numéro de garage");
  if (typeName === "Parking") return $gettext("Numéro de parking");
  if (typeName === "Cave") return $gettext("Numéro de cave");
  if (typeName === "Bureau") return $gettext("Numéro de bureau");
  return $gettext("Numéro d'appartement");
});

const props = defineProps({
  modelValue: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
  index: { type: Number, required: true }
});

const emit = defineEmits(["update:modelValue", "remove"]);

const normalized = reactive({
  ...props.modelValue,
  attributes: {
    details: props.modelValue.attributes?.details ?? null,
    listingPrice: props.modelValue.attributes?.listingPrice ?? null,
    soldWithinThreeYears: props.modelValue.attributes?.soldWithinThreeYears ?? null,
  },
  relationships: {
    property: {
      data: {
        ...props.modelValue.relationships?.property?.data,
        attributes: {
          ...props.modelValue.relationships?.property?.data?.attributes,
          surfaceArea: props.modelValue.relationships?.property?.data?.attributes?.surfaceArea ?? null,
          addressApartmentNumber: props.modelValue.relationships?.property?.data?.attributes?.addressApartmentNumber ?? null,
        },
        relationships: {
          ...props.modelValue.relationships?.property?.data?.relationships,
          propertyType: {
            data: {
              id: props.modelValue.relationships?.property?.data?.relationships?.propertyType?.data?.id ?? null
            }
          }
        }
      }
    }
  }
});

watch(
  normalized,
  (val) => emit("update:modelValue", val),
  { deep: true }
);
</script>
